import { STORAGE_PREFIX } from '../config/app'

/**
 * 로그인(Supabase Auth) 설정.
 *
 * 브라우저에는 **publishable 키만** 내려간다. 이 키로는 RLS 정책이 허락한 것만 할 수 있다:
 * 로그인한 사용자가 자기 행(people, card_collection, talismans, user_results, profiles)을 읽고 쓰는 것.
 * 결과·주문·캐시 같은 서버 전용 테이블은 anon·authenticated 권한 자체가 없다 (마이그레이션 0002).
 */
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? ''
export const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? ''

/**
 * **비밀번호 찾기 메일을 보내는가** (→).
 *
 * 때는 꺼 두었다 — Supabase 기본 메일은 **프로젝트 팀원 주소에만** 보내고 시간당 몇 통으로 막혀 있어
 * (문서 auth-smtp: "Email address not authorized") 손님에게 메일이 가지 않았다.
 * 예전에 메일 서버(**Resend**, 보내는 주소 no-reply@mail.fatemap.net)를 Supabase SMTP 에 붙여 켰다.
 * 메일 링크는 `/auth/reset?token_hash=…&type=recovery` (supabase/templates/reset-password.html).
 * 가입 확인 메일은 그대로 꺼 둔다 (mailer_autoconfirm = true, "비밀번호만 그렇게 하자").
 * 메일 서버가 다시 끊기면 이 한 줄을 `false` 로 — 메일 대신 고객센터·문의 메일 안내가 나온다.
 */
export const PASSWORD_RESET_BY_EMAIL = true

export function authConfigured(): boolean {
  return /^https:\/\/[a-z0-9]{20}\.supabase\.co\/?$/.test(SUPABASE_URL) && SUPABASE_PUBLISHABLE_KEY.startsWith('sb_publishable_')
}

/** supabase-js 가 세션을 두는 localStorage 키 (sb-<ref>-auth-token) */
export function sessionStorageKey(url: string = SUPABASE_URL): string | null {
  const ref = /^https:\/\/([a-z0-9]{20})\.supabase\.co/.exec(url)?.[1]
  return ref ? `sb-${ref}-auth-token` : null
}

/**
 * 켜진 로그인 방식. Supabase 의 공개 설정(/auth/v1/settings)을 읽는다.
 * Google 을 아직 설정하지 않았으면 버튼을 눌러도 오류 페이지로 가지 않게 미리 막는다.
 */
export async function loadProviders(fetchImpl: typeof fetch = fetch): Promise<{ google: boolean; email: boolean }> {
  if (!authConfigured()) return { google: false, email: false }
  try {
    const res = await fetchImpl(`${SUPABASE_URL.replace(/\/$/, '')}/auth/v1/settings`, { headers: { apikey: SUPABASE_PUBLISHABLE_KEY } })
    if (!res.ok) return { google: false, email: true }
    const body = (await res.json()) as { external?: { google?: boolean; email?: boolean } }
    return { google: Boolean(body.external?.google), email: body.external?.email !== false }
  } catch {
    return { google: false, email: true }
  }
}

export const AUTH_CALLBACK_PATH = '/auth/callback'
/** 로그인 뒤 돌아갈 주소. 다른 탭에 섞이지 않게 sessionStorage 에 둔다. */
export const AUTH_RETURN_KEY = `${STORAGE_PREFIX}.authReturn`

/**
 * 로그인 라이브러리를 첫 화면에서 불러올지 정한다.
 * 저장된 세션이 있거나 로그인 복귀 주소로 들어왔을 때만 불러온다. 나머지 방문자는 받지 않는다.
 */
/**
 * **이 브라우저에 남은 세션을 지운다**.
 *
 * supabase-js 의 `signOut()` 은 세션을 읽다 실패하면(만료된 토큰을 새로 받지 못했을 때,
 * 잠금을 못 잡았을 때) **저장된 세션을 그대로 두고** 돌아온다. 화면은 로그아웃으로 바뀌었는데
 * `sb-<ref>-auth-token` 이 남아 있어, 다음 요청과 다음 새로고침이 옛 계정으로 돌아갔다
 * (로그아웃 → 무료로 보기 → 로그인 화면 없이 네이버 이메일 계정).
 * 키와 그 짝(`-code-verifier`, `-user`)을 모두 지운다.
 */
export function forgetStoredSession(): void {
  const key = sessionStorageKey()
  if (!key) return
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i)
      if (k && k.startsWith(key)) window.localStorage.removeItem(k)
    }
  } catch {
    // 저장소를 못 쓰는 환경이면 남은 것도 없다
  }
}

export function hasSessionHint(): boolean {
  if (!authConfigured()) return false
  try {
    if (window.location.pathname === AUTH_CALLBACK_PATH) return true
    const key = sessionStorageKey()
    return Boolean(key && window.localStorage.getItem(key))
  } catch {
    return false
  }
}
